import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Building2, Building, Sparkles, Loader2 } from 'lucide-react'
import { computeFrenchVat } from '@/utils/vat-fr'

/**
 * Résultat renvoyé par l'API recherche-entreprises.api.gouv.fr
 * On ne se base que sur les champs qu'on remplit dans UP-CRM
 */
export interface CompanySuggestion {
  name: string
  siret?: string
  /** TVA intracommunautaire FR calculée depuis le SIREN (format "FR42753108900") */
  vatNumber?: string
  streetAddress?: string
  postalCode?: string
  city?: string
  addressFull?: string
}

interface Props {
  value: string
  onChange: (value: string) => void
  onSelect: (suggestion: CompanySuggestion) => void
  placeholder?: string
  errorClass?: string
  className?: string
}

const API_URL = 'https://recherche-entreprises.api.gouv.fr/search'

const comparable = (s: string) =>
  s.normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim().toUpperCase()

/**
 * `nom_complet` vaut « RAISON SOCIALE (SIGLE) » : la parenthèse n'est gardée
 * que si elle apprend quelque chose. Sans ce tri, on enregistrait
 * « ISOVOO (ISOVOO) » ou « CAPEXTANT CONSEIL (CAPEXTANT) » (constat du
 * 28/10/2026). Un sigle différent (« ECS FRANCE MAGNET ENGINEERING (E.C.S.F.) »)
 * ou plus complet que le nom (« HFB (HFB INVESTISSEMENTS) ») reste.
 */
export function nomSansRedite(nom: string): string {
  const m = nom.match(/^(.*\S)\s*\(([^()]*)\)\s*$/)
  if (!m) return nom.trim()
  const [, principal, entre] = m
  const a = comparable(principal), b = comparable(entre)
  if (!b || a === b || a.startsWith(`${b} `)) return principal.trim()
  return nom.trim()
}
const DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 3
const RESULTS_PER_PAGE = 8

/**
 * Combobox de nom d'entreprise avec autocomplétion via l'API publique
 * recherche-entreprises.api.gouv.fr.
 * Design B : l'utilisateur tape le nom (ou un SIRET 14 chiffres), les
 * résultats s'affichent en dessous, un clic auto-remplit les champs
 * du formulaire via le callback onSelect.
 */
export default function CompanyAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Tapez un nom ou un SIRET…',
  errorClass = '',
  className = '',
}: Props) {
  const [suggestions, setSuggestions] = useState<CompanySuggestion[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeq = useRef(0)

  useEffect(() => {
    // Fermer le dropdown si clic à l'extérieur
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const runSearch = async (q: string) => {
    // Séquence pour éviter que les réponses arrivent dans le désordre
    const mySeq = ++requestSeq.current
    setError(null)
    setIsSearching(true)
    try {
      const url = `${API_URL}?q=${encodeURIComponent(q)}&per_page=${RESULTS_PER_PAGE}`
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()

      // Si une requête plus récente a été lancée, on ignore ce résultat
      if (mySeq !== requestSeq.current) return

      const results: CompanySuggestion[] = (json.results || []).map(
        (r: any): CompanySuggestion => {
          const siege = r.siege || {}
          const name =
            r.nom_complet ||
            r.nom_raison_sociale ||
            siege.enseigne_1 ||
            r.sigle ||
            ''

          // Construire l'adresse rue depuis les champs séparés (plus fiable
          // que de parser geo_adresse qui inclut CP + ville)
          const street = [
            siege.numero_voie,
            siege.indice_repetition,
            siege.type_voie,
            siege.libelle_voie,
          ]
            .filter(Boolean)
            .join(' ')
            .trim()

          return {
            name: nomSansRedite(name).toUpperCase(),
            siret: siege.siret,
            // TVA intracommunautaire calculée depuis le SIREN (siret ou siren)
            vatNumber:
              computeFrenchVat(siege.siret || r.siren) || undefined,
            streetAddress: street || undefined,
            postalCode: siege.code_postal,
            city: siege.libelle_commune,
            addressFull: siege.geo_adresse || undefined,
          }
        },
      )
      setSuggestions(results)
      setIsOpen(true)
    } catch (e: any) {
      if (mySeq !== requestSeq.current) return
      console.error('Erreur recherche entreprise:', e)
      setError('Recherche indisponible pour l\'instant')
      setSuggestions([])
      setIsOpen(true)
    } finally {
      if (mySeq === requestSeq.current) setIsSearching(false)
    }
  }

  const handleChange = (v: string) => {
    onChange(v)
    if (debounceRef.current) clearTimeout(debounceRef.current)

    const cleaned = v.trim()
    // SIRET (14 chiffres) : recherche immédiate
    const strippedSiret = cleaned.replace(/\s/g, '')
    const isSiret = /^\d{14}$/.test(strippedSiret)

    if (isSiret) {
      debounceRef.current = setTimeout(() => runSearch(strippedSiret), DEBOUNCE_MS)
      return
    }
    if (cleaned.length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      setIsOpen(false)
      return
    }
    debounceRef.current = setTimeout(() => runSearch(cleaned), DEBOUNCE_MS)
  }

  const handleSelect = (s: CompanySuggestion) => {
    onSelect(s)
    setIsOpen(false)
  }

  const handleKeepAsIs = () => {
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Building2 className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true)
        }}
        className={`pl-9 pr-9 ${errorClass}`}
        autoComplete="off"
      />
      {isSearching && (
        <Loader2 className="w-4 h-4 text-blue-500 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />
      )}

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 rounded-lg bg-white ring-1 ring-slate-200 shadow-lg divide-y divide-slate-100 z-20 max-h-80 overflow-y-auto"
          style={{ minWidth: 'max(100%, 22rem)', maxWidth: 'min(32rem, calc(100vw - 3rem))' }}
        >
          {suggestions.length > 0 && (
            <>
              {suggestions.map((s, i) => (
                <button
                  key={`${s.siret}-${i}`}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className="w-full text-left p-3 hover:bg-blue-50 flex items-start gap-2.5"
                >
                  <Building className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="text-sm font-semibold text-slate-800 leading-tight break-words">
                      {s.name}
                    </p>
                    {(s.streetAddress || s.postalCode || s.city) && (
                      <p className="text-xs text-slate-500 leading-snug break-words">
                        {[s.streetAddress, s.postalCode, s.city]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                    {s.siret && (
                      <p className="text-[10px] font-mono text-slate-400">
                        SIRET&nbsp;{s.siret}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}
          {suggestions.length === 0 && !isSearching && !error && (
            <div className="p-3 text-xs text-slate-500 text-center italic">
              Aucun résultat trouvé
            </div>
          )}
          {error && (
            <div className="p-3 text-xs text-rose-600 text-center">{error}</div>
          )}
          {value.trim().length > 0 && (
            <button
              type="button"
              onClick={handleKeepAsIs}
              className="w-full p-2 text-xs text-center bg-slate-50 hover:bg-slate-100 text-blue-600 font-medium"
            >
              Utiliser « {value.trim()} » tel quel
            </button>
          )}
        </div>
      )}
    </div>
  )
}
